import React from "react";

import { Mail, Pencil, Trash2 } from "lucide-react";
import moment from "moment";

import { DATE_FORMAT } from "@/src/constants/dateFormat";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  chronologicalColumn,
  dateColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";

import type { TFunction } from "i18next";

import type { FreecopyRow } from "./freecopyColumns.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

interface FreecopyColumnsOptions {
  t: TFunction;
  canWrite: boolean;
  onResendEmail: (freecopy: FreecopyRow) => void;
  onEdit: (freecopy: FreecopyRow) => void;
  onDelete: (freecopy: FreecopyRow) => void;
}

export function freecopyColumns({
  t,
  canWrite,
  onResendEmail,
  onEdit,
  onDelete,
}: FreecopyColumnsOptions): DataTableColumnDef<FreecopyRow>[] {
  return [
    statusColumn<FreecopyRow>("status", t("status"), [
      {
        value: "activated",
        label: t("table:status.activated"),
        tone: "success",
      },
      {
        value: "activable",
        label: t("table:status.activable"),
        tone: "warning",
      },
      { value: "ended", label: t("table:status.ended"), tone: "neutral" },
      { value: "expired", label: t("table:status.expired"), tone: "error" },
    ]),
    {
      accessorKey: "email",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("email")} />
      ),
      meta: { searchable: true, label: t("email") },
      cell: ({ row }) => row.original.email,
    },
    {
      accessorKey: "offer_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("offer")} />
      ),
      meta: { searchable: true, label: t("offer") },
      cell: ({ row }) => row.original.offer_name,
    },
    {
      accessorKey: "code",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("token")} />
      ),
      meta: {
        searchable: true,
        label: t("token"),
        width: "13%",
        minWidth: 127,
        align: "center",
      },
      cell: ({ row }) => (
        <span className="text-muted-foreground font-mono">
          {row.original.code}
        </span>
      ),
    },
    {
      ...chronologicalColumn<FreecopyRow>(
        "activationDate",
        (row) => row.activationDate,
      ),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("activationDate")} />
      ),
      meta: {
        label: t("activationDate"),
        width: "16%",
        minWidth: 190,
      },
      cell: ({ row }) => {
        const {
          status,
          activation_limit_date: limitDate,
          subscription,
        } = row.original;

        if (status === "activable") {
          const expireDate = moment(limitDate).format(DATE_FORMAT);
          return (
            <span
              data-tooltip-id={APP_TOOLTIP_ID}
              data-tooltip-content={t("freeAccessInactive", {
                expire_date: expireDate,
              })}
            >
              {expireDate}
            </span>
          );
        }

        if (!subscription) return null;

        const beginDate = moment(subscription.begin_date).format(DATE_FORMAT);
        const endDate = moment(subscription.end_date).format(DATE_FORMAT);
        return (
          <span
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-content={t("freeAccessActive", {
              begin_date: beginDate,
              end_date: endDate,
            })}
          >
            {`${beginDate} - ${endDate}`}
          </span>
        );
      },
    },
    {
      id: "days_duration",
      accessorFn: (freecopy) => Number(freecopy.days_duration) || 0,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("freeDuration")} />
      ),
      meta: {
        label: t("freeDuration"),
        width: "16%",
        minWidth: 200,
        align: "center",
      },
      cell: ({ getValue }) => {
        const days = getValue<number>();
        return days ? `${days} ${t("days")}` : null;
      },
    },
    dateColumn<FreecopyRow>("updated_at", t("update_date")),
    actionsColumn<FreecopyRow>(
      (freecopy) => {
        const isActivable = freecopy.status === "activable";
        const isEditable = isActivable || freecopy.status === "activated";

        return [
          {
            label: t("resendEmail"),
            icon: <Mail />,
            hidden: !canWrite || !isActivable,
            onClick: () => onResendEmail(freecopy),
          },
          {
            label: t("edit"),
            icon: <Pencil />,
            hidden: !canWrite || !isEditable,
            onClick: () => onEdit(freecopy),
          },
          {
            label: t("delete"),
            icon: <Trash2 />,
            variant: "destructive",
            hidden: !canWrite || !isActivable,
            onClick: () => onDelete(freecopy),
            confirm: {
              title: t("delete"),
              description: t("deleteBody", { freecopy: freecopy.email }),
              confirmLabel: t("confirm"),
            },
          },
        ];
      },
      (freecopy) => freecopy.email,
    ),
  ];
}
